// courseFlow.js — logica lanțului de instruiri periodice (IP) pentru redirectul „prin săgeată".
// După flow-ul de onboarding (IIG→ILM→FP, SSM apoi SU), angajatul e plimbat prin TOATE
// instruirile periodice atribuite (oricâte, de orice tip), una după alta, ca să nu rateze
// niciuna din greșeală. Sursă unică, folosită de SignaturePage și SignJobDescription.

const IP_TYPES = ['IP_SSM', 'IP_SU'];

const isEmployeeSigned = (training) =>
  !!training?.signatures?.some((s) => s.signerRole === 'EMPLOYEE');

// O instruire periodică e „de făcut" dacă: nu are training încă, nu e promovată, SAU e
// promovată dar nesemnată de angajat.
const isIPPending = (assignment) => {
  const tr = assignment?.training;
  if (!tr) return true;
  if (tr.status !== 'PASSED') return true;
  return !isEmployeeSigned(tr);
};

// Ordine stabilă pentru parcurgere: SSM înaintea SU, apoi cele mai vechi (assignedAt) primele.
const IP_ORDER_RANK = { IP_SSM: 0, IP_SU: 1 };
const sortIPs = (a, b) => {
  const r = (IP_ORDER_RANK[a.course?.type] ?? 9) - (IP_ORDER_RANK[b.course?.type] ?? 9);
  if (r !== 0) return r;
  const ta = a.assignedAt ? new Date(a.assignedAt).getTime() : 0;
  const tb = b.assignedAt ? new Date(b.assignedAt).getTime() : 0;
  return ta - tb;
};

/**
 * Returnează { link, label } pentru următoarea instruire periodică de parcurs/semnat, sau null
 * dacă toate sunt gata. Parcurge TOATE IP-urile, nu doar una de fiecare tip.
 *  - IP nepromovat (sau neînceput) → link la parcurgere `/training/:courseId`.
 *  - IP promovat dar nesemnat → link direct la semnare `/training/:trainingId/sign`.
 * @param {Array} assignments - lista din `/courses/my/assigned`
 * @param {{ excludeCourseId?: string }} opts - exclude cursul curent (cel tocmai semnat)
 */
export const getNextPendingIP = (assignments = [], { excludeCourseId } = {}) => {
  const next = assignments
    .filter((a) => IP_TYPES.includes(a.course?.type))
    .filter((a) => a.course?.id !== excludeCourseId)
    .filter(isIPPending)
    .sort(sortIPs)[0];
  if (!next) return null;
  const domain = next.course.type === 'IP_SSM' ? 'SSM' : 'SU';
  const tr = next.training;
  if (tr?.status === 'PASSED' && !isEmployeeSigned(tr) && tr?.id) {
    return { link: `/training/${tr.id}/sign`, label: `Continuă la IP ${domain}` };
  }
  return { link: `/training/${next.course.id}`, label: `Continuă la IP ${domain}` };
};

// Link-ul pentru o instruire periodică (parcurgere sau semnare directă dacă e PASSED nesemnat).
const ipStepLink = (assignment, domain) => {
  const tr = assignment.training;
  if (tr?.status === 'PASSED' && !isEmployeeSigned(tr) && tr?.id) {
    return { link: `/training/${tr.id}/sign`, label: `Continuă la IP ${domain}` };
  }
  return { link: `/training/${assignment.course.id}`, label: `Continuă la IP ${domain}` };
};

// Cea mai veche IP pending dintr-un tip (după assignedAt), excluzând cursul curent.
const oldestPendingIP = (assignments, type, excludeCourseId) =>
  assignments
    .filter((a) => a.course?.type === type)
    .filter((a) => a.course?.id !== excludeCourseId)
    .filter(isIPPending)
    .sort((a, b) => {
      const ta = a.assignedAt ? new Date(a.assignedAt).getTime() : 0;
      const tb = b.assignedAt ? new Date(b.assignedAt).getTime() : 0;
      return ta - tb;
    })[0] || null;

/**
 * Pasul următor în faza periodică, CU continuitate prin Fișa postului (FP).
 * Ordinea canonică: toate IP SSM → FP SSM → toate IP SU → FP SU → null.
 * FP se inserează DOAR dacă e `WAITING_EMPLOYEE` (angajatul trebuie să-l semneze); după ce-l semnează
 * devine `WAITING_COUNTERPART` și pasul se sare automat. Pentru angajații cu onboarding FP e deja
 * semnat acolo (→ skip aici); pentru cei doar-IP, FP apare aici, în continuitate (nu manual).
 * @param {Array} assignments - lista din `/courses/my/assigned`
 * @param {{ ssm?: object, su?: object }} flow - răspunsul din `/job-descriptions/my-flow`
 * @param {{ excludeCourseId?: string }} opts
 */
export const getNextPendingStep = (assignments = [], flow = {}, { excludeCourseId } = {}) => {
  const fpStep = (domain) => {
    const fp = domain === 'SSM' ? flow?.ssm : flow?.su;
    if (fp && fp.instanceId && fp.status === 'WAITING_EMPLOYEE') {
      return {
        link: `/job-descriptions/${fp.instanceId}/sign`,
        label: `Continuă la Fișa postului ${domain}`,
      };
    }
    return null;
  };

  const ssmIP = oldestPendingIP(assignments, 'IP_SSM', excludeCourseId);
  if (ssmIP) return ipStepLink(ssmIP, 'SSM');
  const fpSsm = fpStep('SSM');
  if (fpSsm) return fpSsm;
  const suIP = oldestPendingIP(assignments, 'IP_SU', excludeCourseId);
  if (suIP) return ipStepLink(suIP, 'SU');
  const fpSu = fpStep('SU');
  if (fpSu) return fpSu;
  return null;
};
